import { useState, type ReactNode } from 'react'
import { Trash2, Pencil, Plus, Server, Building2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, EmptyState } from '@/components/States'
import { fmtBytes } from './infraApi'
import { useLoad, RefreshBtn, Rows, Stat, Gauge, fmtDuration } from './infraShared'
import {
  fetchHost,
  fetchServersRegistry,
  createServer,
  updateServer,
  deleteServer,
  fetchDataCenters,
  IX,
  type HostInfo,
  type ServerRow,
  type ServerInput,
  type DataCentersResp,
} from './infraExtApi'

/* Live-host karta — A17.02 va A17.04 ishlatadi. */
function HostCard({ h }: { h: HostInfo }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Server size={16} className="text-accent" />
        <span className="text-sm font-medium text-primary">{IX.liveNode}</span>
        <span className="ml-auto flex items-center gap-1 text-xs text-success">
          <span aria-hidden>●</span>active
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
        <Stat label={IX.hostname} value={h.hostname || '—'} />
        <Stat label={IX.os} value={`${h.os}/${h.arch}`} />
        <Stat label={IX.cpu} value={String(h.cpu_cores)} />
        <Stat label={IX.load} value={`${h.load1.toFixed(2)} / ${h.load5.toFixed(2)} / ${h.load15.toFixed(2)}`} />
        <Stat
          label={IX.memory}
          value={h.mem_total > 0 ? `${fmtBytes(h.mem_total - h.mem_available)} / ${fmtBytes(h.mem_total)}` : '—'}
        />
        <Stat label={IX.disk} value={h.disk_total > 0 ? `${fmtBytes(h.disk_total - h.disk_free)} / ${fmtBytes(h.disk_total)}` : '—'} />
        <Stat label={IX.uptime} value={fmtDuration(h.host_uptime_s)} />
        <Stat label={IX.procUptime} value={fmtDuration(h.proc_uptime_s)} />
        <Stat label={IX.goroutines} value={h.goroutines.toLocaleString()} />
        <Stat label={IX.activeConns} value={String(h.active_conns)} />
        <Stat label={IX.dbPool} value={`${h.db_pool_acquired} / ${h.db_pool_total} / ${h.db_pool_max}`} />
        <Stat label="Go" value={h.go_version.replace('go', '')} />
      </div>
    </div>
  )
}

const EMPTY_SERVER: ServerInput = {
  name: '',
  role: 'app',
  region: '',
  datacenter: 'primary',
  hostname: '',
  ip_addr: '',
  status: 'active',
  notes: '',
}
const ROLES = ['app', 'db', 'cache', 'storage', 'edge', 'worker']
const STATUSES = ['active', 'maintenance', 'offline']

/* ── A17.02 — Server ro'yxati ─────────────────────────────────── */
export function InfraServersScreen() {
  const { toast } = useToast()
  const host = useLoad<HostInfo>(fetchHost)
  const reg = useLoad<ServerRow[]>(fetchServersRegistry)
  const [form, setForm] = useState<ServerInput | null>(null)
  const [editId, setEditId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const openAdd = () => {
    setEditId(null)
    setForm({ ...EMPTY_SERVER })
  }
  const openEdit = (r: ServerRow) => {
    setEditId(r.id)
    setForm({
      name: r.name, role: r.role, region: r.region, datacenter: r.datacenter,
      hostname: r.hostname, ip_addr: r.ip_addr, status: r.status, notes: r.notes,
    })
  }
  const save = async () => {
    if (!form || !form.name.trim()) {
      toast(IX.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      if (editId) await updateServer(editId, form)
      else await createServer(form)
      toast(IX.done, { tone: 'success' })
      setForm(null)
      setEditId(null)
      reg.reload()
    } catch {
      toast(IX.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const del = async (id: number) => {
    try {
      await deleteServer(id)
      reg.reload()
    } catch {
      toast(IX.failed, { tone: 'error' })
    }
  }

  const field = (label: string, node: ReactNode) => (
    <label className="text-xs text-tertiary">
      {label}
      {node}
    </label>
  )
  const inputCls = 'mt-1 block w-full rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary'

  return (
    <>
      <PageHeader
        title={IX.servers}
        screenId="A17.02"
        description={IX.serversDesc}
        actions={
          <div className="flex gap-2">
            <button type="button" onClick={openAdd} className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
              <Plus size={14} />
              {IX.addServer}
            </button>
            <RefreshBtn onClick={() => { host.reload(); reg.reload() }} />
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
        <div className="mx-auto max-w-5xl space-y-4">
          {host.err ? <ErrorState code={host.err.code} onRetry={host.reload} /> : !host.data ? <Rows n={3} /> : <HostCard h={host.data} />}

          {form && (
            <div className="rounded-lg border border-accent/40 bg-surface-2 p-4">
              <p className="mb-3 text-sm font-medium text-primary">{editId ? IX.editServer : IX.addServer}</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {field(IX.serverName, <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />)}
                {field(
                  IX.role,
                  <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>,
                )}
                {field(
                  IX.status,
                  <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>,
                )}
                {field(IX.datacenter, <input className={inputCls} value={form.datacenter} onChange={(e) => setForm({ ...form, datacenter: e.target.value })} />)}
                {field(IX.region, <input className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />)}
                {field(IX.ipAddr, <input className={inputCls} value={form.ip_addr} onChange={(e) => setForm({ ...form, ip_addr: e.target.value })} />)}
                {field(IX.hostname, <input className={inputCls} value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })} />)}
                <div className="col-span-2 sm:col-span-3">
                  {field(IX.notes, <input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />)}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">
                  {IX.save}
                </button>
                <button type="button" onClick={() => { setForm(null); setEditId(null) }} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">
                  {IX.cancel}
                </button>
              </div>
            </div>
          )}

          {reg.err ? (
            <ErrorState code={reg.err.code} onRetry={reg.reload} />
          ) : !reg.data ? (
            <Rows n={3} />
          ) : reg.data.length === 0 ? (
            <EmptyState icon={<Server size={26} strokeWidth={1.5} />} title={IX.noServers} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.serverName}</th>
                    <th className="px-4 py-2 font-medium">{IX.role}</th>
                    <th className="px-4 py-2 font-medium">{IX.datacenter}</th>
                    <th className="px-4 py-2 font-medium">{IX.ipAddr}</th>
                    <th className="px-4 py-2 font-medium">{IX.status}</th>
                    <th className="px-4 py-2 text-right font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {reg.data.map((r) => (
                    <tr key={r.id} className="border-t border-border-subtle">
                      <td className="px-4 py-2 text-primary">{r.name}</td>
                      <td className="px-4 py-2 font-mono text-secondary">{r.role}</td>
                      <td className="px-4 py-2 text-secondary">{r.datacenter}</td>
                      <td className="px-4 py-2 font-mono text-tertiary">{r.ip_addr || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${r.status === 'active' ? 'bg-success/10 text-success' : r.status === 'offline' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button type="button" onClick={() => openEdit(r)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-primary" title={IX.edit}>
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={IX.delete}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── A17.03 — Data-markazlar (DC) ─────────────────────────────── */
export function InfraDataCentersScreen() {
  const { data, err, reload } = useLoad<DataCentersResp>(fetchDataCenters)
  return (
    <>
      <PageHeader title={IX.dc} screenId="A17.03" description={IX.dcDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto max-w-4xl space-y-3">
            {data.datacenters.map((dc) => (
              <div key={dc.name} className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-accent" />
                  <span className="text-sm font-medium text-primary">{dc.name}</span>
                  {dc.is_primary && <span className="rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent">{IX.primary}</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                  <Stat label={IX.serversCount} value={String(dc.servers)} />
                  <Stat label={IX.activeCount} value={String(dc.active_servers)} tone="success" />
                  <Stat label={IX.regions} value={dc.regions || '—'} />
                  {dc.live_node && <Stat label={IX.hostname} value={dc.live_node.hostname || '—'} />}
                </div>
                {dc.live_node && (
                  <p className="mt-2 text-xs text-tertiary">
                    {IX.liveNode}: {dc.live_node.os} · {dc.live_node.cpu_cores} CPU · load {dc.live_node.load1.toFixed(2)} · mem {dc.live_node.mem_used_pct.toFixed(0)}% · disk {dc.live_node.disk_used_pct.toFixed(0)}%
                  </p>
                )}
              </div>
            ))}
            {data.single_node && <p className="text-center text-xs text-tertiary">{IX.singleNodeNote}</p>}
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.04 — DC yuklamasi va taqsimoti ───────────────────────── */
export function InfraDcLoadScreen() {
  const { data, err, reload } = useLoad<HostInfo>(fetchHost)
  return (
    <>
      <PageHeader title={IX.dcLoad} screenId="A17.04" description={IX.dcLoadDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            <div className="grid gap-2.5 sm:grid-cols-3">
              <Gauge label={IX.cpuLoad} pct={data.load_per_core * 100} />
              <Gauge label={IX.memUsed} pct={data.mem_used_pct} />
              <Gauge label={IX.diskUsed} pct={data.disk_used_pct} />
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              <Stat label={IX.cpu} value={String(data.cpu_cores)} />
              <Stat label={IX.load} value={`${data.load1.toFixed(2)} / ${data.load5.toFixed(2)} / ${data.load15.toFixed(2)}`} />
              <Stat label={IX.activeConns} value={String(data.active_conns)} />
              <Stat label={IX.dbPool} value={`${data.db_pool_acquired} / ${data.db_pool_max}`} />
              <Stat label={IX.memory} value={data.mem_total > 0 ? `${fmtBytes(data.mem_total - data.mem_available)} / ${fmtBytes(data.mem_total)}` : '—'} />
              <Stat label={IX.disk} value={data.disk_total > 0 ? `${fmtBytes(data.disk_total - data.disk_free)} / ${fmtBytes(data.disk_total)}` : '—'} />
              <Stat label={IX.uptime} value={fmtDuration(data.host_uptime_s)} />
              <Stat label={IX.goroutines} value={data.goroutines.toLocaleString()} />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.05 — Foydalanuvchini DC'ga biriktirish ───────────────── */
export function InfraDcAssignmentScreen() {
  const { data, err, reload } = useLoad<DataCentersResp>(fetchDataCenters)
  return (
    <>
      <PageHeader title={IX.dcAssign} screenId="A17.05" description={IX.dcAssignDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={2} />
        ) : (
          <div className="mx-auto max-w-2xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-8 text-center">
              <p className="text-sm text-tertiary">{IX.allUsers}</p>
              <p className="mt-2 text-5xl font-bold text-primary tabular">{data.user_count.toLocaleString()}</p>
              <p className="mt-2 text-sm text-secondary">
                100% {IX.ofUsers} <span className="font-medium text-primary">{data.primary}</span>
              </p>
            </div>
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.datacenter}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.assignedTo}</th>
                    <th className="px-4 py-2 text-right font-medium">%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.datacenters.map((dc) => (
                    <tr key={dc.name} className="border-t border-border-subtle">
                      <td className="px-4 py-2 text-primary">
                        {dc.name}
                        {dc.is_primary && <span className="ml-2 rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent">{IX.primary}</span>}
                      </td>
                      <td className="px-4 py-2 text-right tabular text-secondary">{dc.is_primary ? data.user_count.toLocaleString() : '0'}</td>
                      <td className="px-4 py-2 text-right tabular text-secondary">{dc.is_primary ? '100' : '0'}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-xs text-tertiary">{IX.singleNodeNote}</p>
          </div>
        )}
      </div>
    </>
  )
}
